import React, { Suspense, useMemo } from 'react';
import { noop } from 'lodash';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { DialogComponent } from '@/components/DialogComponent';
import Loader from '@/components/Loader';
import useNavigation from '@/hooks/useNavigation';
import { useCourseEditorContext } from '@/modules/corporate/modules/course/views/CourseBuilder/context';
import { QUIZ_PLAYER_BOTTOMSHEET_CLASSES } from '@/modules/corporate/modules/quiz/views/QuizDetails/components/QuizPlayerDialog/styles';
import QuizPlayer from '@/modules/corporate/modules/quiz/views/QuizPlayer';
import { DIALOG_CLASSES } from '@/modules/corporate/modules/quiz/views/QuizPlayer/utils/const';
import { FULL_PAGE_PLAYER_CLASSES } from '@/modules/corporate/modules/quiz/views/QuizPlayer/utils/styles';

import { QuizPlayerContainerProps } from '../../types';
import PopupQuizCard from './components/PopupQuizCard';
import PopupQuizPreview from './components/PopupQuizPreview';
import usePopupQuiz from './hooks/usePopupQuiz';
import { mobileDialogPaperStyles, quizCardMobileStyles, quizCardStyles } from './styles';
const BOTTOMSHEET_CLASSES = {
  ...QUIZ_PLAYER_BOTTOMSHEET_CLASSES,
  paper: cn(QUIZ_PLAYER_BOTTOMSHEET_CLASSES.paper, mobileDialogPaperStyles, 'rounded-8'),
};

function PopupQuizContainer(props: QuizPlayerContainerProps) {
  const {
    courseId,
    userId,
    contentId,
    onQuizSubmit,
    onEditQuiz,
    quiz,
    quizPerformance,
    isLocalizedContent,
  } = props;
  const { t } = useTranslation();
  const { isMobile } = useScreen();
  const navigation = useNavigation();
  const quizInCourseBuilder =
    navigation.route.endsWith('build') || navigation.route.includes('lyearn-ai');
  const { onClickQuiz } = useCourseEditorContext(); //for AI course creator

  const onClickBuilder = onClickQuiz ? () => onClickQuiz(quiz.id) : noop;

  const popupQuizData = usePopupQuiz({
    parentEntityId: courseId,
    quizPerformance,
    quiz,
  });
  const { showModal, showQuizPlayer, onOpen, onClose, quizPlayerProps } = popupQuizData;
  const handleClick = quizInCourseBuilder ? onEditQuiz ?? onClickBuilder : onOpen;

  const actionLabelOverride = useMemo(
    () => (quizInCourseBuilder && onEditQuiz ? t('Course:Builder.EditQuiz') : undefined),
    [quizInCourseBuilder, onEditQuiz, t],
  );

  return (
    <>
      <PopupQuizCard
        className={cn('cursor-pointer', isMobile ? quizCardMobileStyles : quizCardStyles)}
        overrideActionLabel={actionLabelOverride}
        quiz={quiz}
        quizPerformance={quizPerformance}
        onClick={handleClick}
      />
      {showModal ? (
        <DialogComponent
          bottomSheetClasses={BOTTOMSHEET_CLASSES}
          bottomSheetContainerClassName="mb-0 h-full"
          classes={DIALOG_CLASSES}
          closeButton={{ className: 'me-12 mt-12', size: 'medium' }}
          open={showModal}
          showCloseIcon={false}
          onClose={onClose}>
          {!showQuizPlayer ? (
            <PopupQuizPreview
              courseId={courseId}
              popupQuizData={popupQuizData}
              quiz={quiz}
              quizPerformance={quizPerformance}
            />
          ) : (
            <Suspense fallback={<Loader />}>
              <QuizPlayer
                isEmbed
                classes={FULL_PAGE_PLAYER_CLASSES}
                isLocalizedContent={isLocalizedContent}
                parentEntityId={courseId}
                quizId={quiz.id}
                userId={userId}
                onQuizSubmit={onQuizSubmit}
                {...quizPlayerProps}
              />
            </Suspense>
          )}
        </DialogComponent>
      ) : null}
    </>
  );
}

export default PopupQuizContainer;
